// Share/Audit Modal - Display raw AI response, Q&A, and debug info
import { useState } from 'react';
import { Text, Button, Card } from './ui';
import Icons from './Icons';

function ShareAuditModal({
    isOpen,
    onClose,
    analysisResults,
    rawAIResponse,
    questionnaireAnswers,
    capturedImages,
    modelUsed
}) {
    const [activeTab, setActiveTab] = useState('summary');
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const handleCopyToClipboard = async () => {
        const auditData = {
            timestamp: new Date().toISOString(),
            model: modelUsed || analysisResults?._model || 'Unknown',
            questionnaireAnswers,
            rawAIResponse,
            parsedResults: analysisResults,
        };

        try {
            await navigator.clipboard.writeText(JSON.stringify(auditData, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: 'Thiệt Chuẩn AI - Kết quả phân tích',
            text: `Điểm sức khỏe: ${analysisResults?.overallScore || 'N/A'}/100\nChứng hậu: ${analysisResults?.syndrome?.primary?.name || 'N/A'}`,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.error('Share failed:', err);
            }
        } else {
            handleCopyToClipboard();
        }
    };

    const tabs = [
        { id: 'summary', label: 'Tóm tắt' },
        { id: 'qa', label: 'Hỏi đáp' },
        { id: 'raw', label: 'Raw AI' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div
                className="w-full max-w-lg max-h-[85vh] bg-theme-card rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-theme">
                    <Text className="font-semibold text-lg">Chi tiết phân tích</Text>
                    <button onClick={onClose} className="p-2 hover:bg-theme-secondary rounded-full transition-colors">
                        <Icons.Close />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-theme">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === tab.id
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-theme-tertiary'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {activeTab === 'summary' && (
                        <>
                            <Card className="!p-4">
                                <Text variant="small" className="text-theme-tertiary mb-1">Model AI</Text>
                                <Text className="font-medium">{modelUsed || analysisResults?._model || 'Unknown'}</Text>
                            </Card>
                            <Card className="!p-4">
                                <Text variant="small" className="text-theme-tertiary mb-1">Điểm tổng quát</Text>
                                <Text className="font-bold text-2xl text-primary">{analysisResults?.overallScore || 'N/A'}/100</Text>
                            </Card>
                            <Card className="!p-4">
                                <Text variant="small" className="text-theme-tertiary mb-1">Chứng hậu chính</Text>
                                <Text className="font-medium">{analysisResults?.syndrome?.primary?.name || 'N/A'}</Text>
                            </Card>
                            <Card className="!p-4">
                                <Text variant="small" className="text-theme-tertiary mb-2">Hình ảnh đã chụp</Text>
                                <div className="flex gap-2 flex-wrap">
                                    {Object.entries(capturedImages || {}).map(([type, src]) => (
                                        src && (
                                            <img
                                                key={type}
                                                src={src}
                                                alt={type}
                                                className="w-16 h-16 object-cover rounded-lg border border-theme"
                                            />
                                        )
                                    ))}
                                </div>
                            </Card>
                        </>
                    )}

                    {activeTab === 'qa' && (
                        <div className="space-y-3">
                            {Object.entries(questionnaireAnswers || {}).length > 0 ? (
                                Object.entries(questionnaireAnswers).map(([key, value]) => (
                                    <Card key={key} className="!p-3">
                                        <Text variant="small" className="text-theme-tertiary mb-1">{key}</Text>
                                        <Text className="font-medium">
                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                        </Text>
                                    </Card>
                                ))
                            ) : (
                                <Text variant="caption" className="text-center py-8">Không có dữ liệu câu hỏi</Text>
                            )}
                        </div>
                    )}

                    {activeTab === 'raw' && (
                        <div className="space-y-3">
                            <Text variant="small" className="text-theme-tertiary">Raw AI Response (expandable)</Text>
                            <div className="bg-theme-secondary rounded-xl p-3 overflow-x-auto">
                                <pre className="text-xs text-theme-secondary whitespace-pre-wrap break-all font-mono">
                                    {rawAIResponse
                                        ? (typeof rawAIResponse === 'string'
                                            ? rawAIResponse
                                            : JSON.stringify(rawAIResponse, null, 2))
                                        : JSON.stringify(analysisResults, null, 2)
                                    }
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 p-4 border-t border-theme">
                    <Button variant="secondary" onClick={handleCopyToClipboard} className="flex-1">
                        {copied ? '✓ Đã sao chép' : '📋 Sao chép'}
                    </Button>
                    <Button onClick={handleShare} className="flex-1">
                        📤 Chia sẻ
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default ShareAuditModal;
